import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { fmt } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Layers, Loader2, Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminFees() {
  const [plans, setPlans] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [rows, setRows] = useState([{ name: "", amount: "" }]);
  const [editPlan, setEditPlan] = useState(null);
  const [delPlan, setDelPlan] = useState(null);

  const load = () => {
    setPlans(null);
    api.get("/subscription-plans").then((r) => setPlans(r.data))
      .catch((e) => { toast.error(apiErr(e.response?.data?.detail)); setPlans([]); });
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setRows([{ name: "", amount: "" }]); setAddOpen(true); };
  const addRow = () => setRows((rs) => [...rs, { name: "", amount: "" }]);
  const removeRow = (i) => setRows((rs) => (rs.length === 1 ? rs : rs.filter((_, idx) => idx !== i)));
  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const saveAdd = async () => {
    const clean = rows.map((r) => ({ name: (r.name || "").trim(), amount: Number(r.amount || 0) })).filter((r) => r.name);
    if (!clean.length) { toast.error("Enter at least one plan name"); return; }
    setSaving(true);
    try {
      await api.post("/subscription-plans", { plans: clean });
      toast.success(`${clean.length} plan(s) added`);
      setAddOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!(editPlan.name || "").trim()) { toast.error("Enter plan name"); return; }
    setSaving(true);
    try {
      await api.put(`/subscription-plans/${editPlan.id}`, { name: editPlan.name.trim(), amount: Number(editPlan.amount || 0) });
      toast.success("Plan updated");
      setEditPlan(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/subscription-plans/${delPlan.id}`);
      toast.success("Plan deleted");
      setDelPlan(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="subscription-plans-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><Layers className="text-indigo-600" />Subscription Plans</h1>
          <p className="text-slate-500 text-sm mt-1">Define the subscription plans offered to schools on the platform.</p>
        </div>
        <Button onClick={openAdd} className="gap-1.5" data-testid="add-plan-btn"><Plus size={16} />Add Plan</Button>
      </div>

      {plans === null ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>
      ) : plans.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 py-16 text-center text-slate-400" data-testid="plans-empty">No subscription plans yet. Click &ldquo;Add Plan&rdquo; to create one.</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="plans-table">
          <table className="w-full text-sm min-w-[480px]">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3 w-16">Se. No.</th>
                <th className="text-left px-4 py-3">Plan Name</th>
                <th className="text-left px-4 py-3">Amount</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {plans.map((p, idx) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`plan-row-${idx}`}>
                  <td className="px-4 py-3 text-slate-400">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-800" data-testid={`plan-name-${idx}`}>{p.name}</td>
                  <td className="px-4 py-3 text-slate-700" data-testid={`plan-amount-${idx}`}>{fmt(p.amount)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" className="gap-1 text-indigo-600" onClick={() => setEditPlan({ ...p })} data-testid={`edit-plan-${idx}`}><Pencil size={14} />Edit</Button>
                    <Button size="sm" variant="ghost" className="gap-1 text-rose-600" onClick={() => setDelPlan(p)} data-testid={`delete-plan-${idx}`}><Trash2 size={14} />Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="add-plan-dialog">
          <DialogHeader><DialogTitle>Subscription Plans</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-12 gap-2 text-xs font-semibold uppercase text-slate-400 px-1">
              <div className="col-span-6">Plan Name</div>
              <div className="col-span-5">Amount</div>
              <div className="col-span-1" />
            </div>
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`plan-input-row-${i}`}>
                <Input className="col-span-6" placeholder="e.g. Premium" value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} data-testid={`plan-name-input-${i}`} />
                <Input className="col-span-5" type="number" placeholder="0" value={r.amount} onChange={(e) => setRow(i, { amount: e.target.value })} data-testid={`plan-amount-input-${i}`} />
                <button type="button" onClick={() => removeRow(i)} disabled={rows.length === 1} className="col-span-1 flex items-center justify-center text-slate-400 hover:text-rose-500 disabled:opacity-30" data-testid={`remove-plan-row-${i}`}><X size={16} /></button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={addRow} data-testid="add-plan-row-btn"><Plus size={14} />Add More</Button>
          </div>
          <DialogFooter><Button onClick={saveAdd} disabled={saving} data-testid="save-plans-btn">{saving ? <Loader2 className="animate-spin" size={16} /> : "Save Plans"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editPlan} onOpenChange={(o) => { if (!o) setEditPlan(null); }}>
        <DialogContent className="max-w-md" data-testid="edit-plan-dialog">
          <DialogHeader><DialogTitle>Edit Plan</DialogTitle></DialogHeader>
          {editPlan && (
            <div className="space-y-3">
              <div><Label>Plan Name</Label><Input value={editPlan.name} onChange={(e) => setEditPlan({ ...editPlan, name: e.target.value })} data-testid="edit-plan-name-input" /></div>
              <div><Label>Amount</Label><Input type="number" value={editPlan.amount} onChange={(e) => setEditPlan({ ...editPlan, amount: e.target.value })} data-testid="edit-plan-amount-input" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} disabled={saving} data-testid="save-edit-plan-btn">{saving ? <Loader2 className="animate-spin" size={16} /> : "Update Plan"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delPlan} onOpenChange={(o) => { if (!o) setDelPlan(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-plan-dialog">
          <DialogHeader><DialogTitle>Delete Plan</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Delete plan <b>{delPlan?.name}</b>? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelPlan(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="confirm-delete-plan-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
